import { ProductGroup } from '@app/core/models/client';
import {
  defaultBrandingData,
  Header,
  i18nLocal,
  StyleProvider,
  Verification,
} from '@relaynetwork/design-system';
import { VerificationFieldConfig } from '@relaynetwork/rn-node-feed-api-types';
import React, { useEffect, useMemo } from 'react';

export type VerificationPagePreviewProps = {
  pswEnabled: ProductGroup['password']['enabled'];
  pswTCS: ProductGroup['password']['tcs'];
  /**
   * Legacy raw-gating inputs used by the config + translations editors. When
   * `preGated` is false (default), the component derives pswTCS/pswTCMandatory/
   * consentTSCS visibility from these, preserving today's editor behavior.
   */
  showPswTCS?: boolean;
  showTCS?: boolean;
  /**
   * When true, pswTCS/pswTCMandatory/consentTSCS are already gated upstream
   * (via computeProductGroupBranding in resolveVerificationPreviewProps) and are
   * passed straight through. Used by the PDF-preview Verification screen.
   */
  preGated?: boolean;
  /** Renders the post-verification error state (DS `Verification`). */
  postVerificationError?: boolean;
  fields: VerificationFieldConfig[];
  pswTCMandatory: ProductGroup['password']['tc_mandatory'];
  logoUrl: ProductGroup['branding']['icon_s3_url'];
  color: ProductGroup['branding']['color'];
  showProductGroup: ProductGroup['hide_pg_wire_display_name'];
  productGroupName: ProductGroup['name'];
  productGroupDescription: ProductGroup['description'];
  fdicSignage: ProductGroup['fdic'];
  showFdic: boolean;
  language?: string;
  showLogo?: boolean;
  showTitleBar?: boolean;
  showBanner?: boolean;
  bannerUrl?: string;
  bannerAltText?: string;
  consentTSCS?: string;
  verificationBody: string;
  verificationButtonLabel: string;
  verificationHeader?: string;
  /**
   * Product group es overrides (`i18n.es`). When provided (PDF-preview path),
   * they are registered as the DS i18next `es` bundle so the `Verification`
   * organism resolves `translations.onboarding.verification.*` /
   * `translations.password.*` itself, applying fallbackLng: 'en' over the base
   * values passed above. Editors omit it and keep prop-verbatim rendering.
   */
  i18nEs?: NonNullable<ProductGroup['i18n']>['es'];
};

export function VerificationPagePreview({
  pswTCS,
  pswEnabled,
  showPswTCS = true,
  consentTSCS,
  showTCS = true,
  preGated = false,
  postVerificationError = false,
  pswTCMandatory,
  fields,
  logoUrl,
  color,
  showProductGroup,
  productGroupName,
  productGroupDescription,
  fdicSignage,
  showFdic = true,
  language = 'en',
  showLogo = true,
  showTitleBar = true,
  // Defaults to `true` to match `computeProductGroupBranding`'s own
  // `?? true` fallback and rn-feed-webapp's real `handleShowBanner()`
  // ("unset config" means "show"), not `false` — see DIS-1726.
  showBanner = true,
  bannerUrl = '',
  bannerAltText = '',
  verificationBody = '',
  verificationButtonLabel = '',
  verificationHeader = '',
  i18nEs,
}: VerificationPagePreviewProps) {
  // Initialize i18n, register the product group's es overrides (so the DS
  // organism resolves them via fallbackLng: 'en'), then set the language.
  // `init()` resets resources to the DS defaults, so the bundle must be added
  // after it and before changeLanguage (same order as WirePreview).
  useEffect(() => {
    i18nLocal.init();
    if (i18nEs) {
      i18nLocal.addResourceBundle('es', 'common', i18nEs, true, true);
    }
    i18nLocal.changeLanguage(language);

    return () => {
      i18nLocal.init();
      i18nLocal.changeLanguage('en');
    };
  }, [language, i18nEs]);

  const updatedVerificationFields = fields.map((field) => {
    const updatedField = { ...field, required: true, type: 'text' };
    switch (field.field) {
      case 'ssn_last4':
        return { ...updatedField, type: 'ssn' };
      case 'postal_code':
        return { ...updatedField, type: 'postalcode' };
      default:
        return updatedField;
    }
  });
  const brandingData = useMemo(
    () => ({
      ...defaultBrandingData,
      color: color,
    }),
    [color],
  );
  return (
    <>
      <StyleProvider brandingData={brandingData}>
        <Header
          productGroupDescription={productGroupDescription}
          productGroupName={showProductGroup ? productGroupName : ' '}
          showTitleNavBar={showTitleBar}
          logoUrl={logoUrl}
          showRegularNavBar
          showLogo={showLogo}
          showBanner={showBanner}
          bannerUrl={bannerUrl}
          bannerAltText={bannerAltText}
          isPreview={true}
          isMobile
          isPortrait={true}
          fdic={fdicSignage}
          showFDICSignageOnPassword={showFdic}
        />
        <Verification
          isMobile={false}
          consentType="express"
          isLockedOut={false}
          pswTCMandatory={
            preGated
              ? pswTCMandatory
              : pswEnabled
                ? showTCS && pswTCMandatory
                : pswTCMandatory
          }
          pswTCS={
            preGated ? pswTCS : !pswEnabled && showPswTCS ? pswTCS : undefined
          }
          consentTSCS={
            preGated
              ? consentTSCS
              : !pswEnabled && showTCS
                ? consentTSCS
                : undefined
          }
          showAcceptanceOption={!pswEnabled}
          postVerificationError={postVerificationError}
          fields={updatedVerificationFields}
          handleSubmit={undefined}
          verificationBody={verificationBody}
          verificationButtonLabel={verificationButtonLabel}
          verificationHeader={verificationHeader}
        />
      </StyleProvider>
    </>
  );
}
